/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import axios from 'axios';
import { getStore } from 'store/accessor';
import http from './axios';
import { dashboardActions } from '../../dashboard/slice';

export class ResponseError extends Error {
  constructor(error) {
    super(error?.message ?? 'Request failed');
    this.code = error?.code;
  }
}
/**
 * Whether a rejection came from an aborted request (AbortController / axios cancel)
 */
export const isRequestCanceled = (error) => axios.isCancel(error) || error?.code === 'ERR_CANCELED';
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object} The parsed JSON from the request
 */
const parseJSON = (response) => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.data;
};
/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
const errorHandling = (error) => {
  if (isRequestCanceled(error)) throw error;
  const response = error?.response;
  const errorResponse = new ResponseError(error);
  errorResponse.status = response?.status ?? null;
  errorResponse.message = response?.data ?? error?.message ?? 'Network error';
  if (response?.status === 401 && response?.data === 'TOKEN_REVOCATION') {
    getStore()?.dispatch(dashboardActions.setTokenRevocationError(true));
  }
  throw errorResponse;
};
/**
 * Sends a request through the configured axios instance, returning a promise
 *
 * @param  {object}  metadata            Request metadata: path (with `:param` placeholders),
 *                                      method, and optional baseURL
 * @param  {object}  data                Payload; values matching `:param` placeholders are
 *                                      substituted into the path, the rest is sent as the body
 * @param  {boolean} [isSecure=true]     Whether the request requires an Authorization header
 * @param  {boolean} [multipart=false]   Send the payload as multipart form data
 * @param  {object}  [options]           Additional request options
 * @param  {AbortSignal} [options.signal] Signal to cancel the in-flight request
 * @return {object}                      The response data
 */
const request = async (_metadata, data, isSecure = true, multipart = false, { signal } = {}) => {
  const payload = { ...data };
  const metadata = { ..._metadata };
  //
  metadata.path = metadata.path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (match, key) => {
    if (key === 'query') {
      // pre-built query string, substituted without encoding
      const value = payload[key];
      delete payload[key];
      return value == null ? '' : String(value);
    }
    const value = payload[key];
    if (value === undefined || value === null) {
      throw new ResponseError({
        code: 'INVALID_PATH_PARAM',
        message: `Missing path parameter ":${key}" for ${metadata.method} ${_metadata.path}`,
      });
    }
    delete payload[key];
    return encodeURIComponent(String(value));
  });
  let requestBody = JSON.stringify(payload);
  if (multipart) {
    const formData = new FormData();
    formData.append('file', payload.file);
    requestBody = formData;
  }
  const options = {
    method: metadata.method,
    // mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache',
    url: metadata.path,
    ...(metadata.baseURL && { baseURL: metadata.baseURL }),
    // credentials: 'include', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
      ...(isSecure && {
        Authorization: '',
      }),
      ...(multipart && {
        enctype: 'multipart/form-data',
      }),
    },
    redirect: 'follow', // manual, *follow, error
    referrer: 'no-referrer', // no-referrer, *client
    ...(['POST', 'PUT', 'PATCH'].includes(metadata.method) && {
      data: requestBody,
    }),
    ...(signal && { signal }), // Attach AbortController signal to allow request cancellation
  };
  //
  try {
    const response = await http(options);
    return parseJSON(response);
  } catch (error) {
    return errorHandling(error);
  }
};
//
export default request;
